import type { DatePeriod } from '@/utils/date';
import {
  parseNumberFilter,
  parseNumberListFilter,
  parseTextFilter,
  toFilterText,
  toGeoFilterValues,
  toGeoSearch,
  toNumberListFilterText,
  toPeriodFilterValues,
  toPeriodSearch,
} from '@/utils/filters';
import type {
  MpApprovalSearch,
  MpRegionSearch,
  MpReportSearch,
} from '../types/marketPromotion.types';
import type {
  DonationBatchSearch,
  DonationBudgetSearch,
  DonationRequisitionSearch,
} from '../types/marketPromotionP2.types';

type FilterValues = Record<string, string>;

type DonationApprovalFilterSearch = Pick<
  MpApprovalSearch,
  | 'dateFrom'
  | 'dateTo'
  | 'enteredBy'
  | 'enteredMobile'
  | 'instituteEiin'
  | 'teacherMobile'
  | 'promoItemIds'
  | 'approveTypeNos'
>;

function toPeriodValues(
  search: DonationApprovalFilterSearch,
  defaultPeriod: DatePeriod | undefined,
): FilterValues {
  if (defaultPeriod) return toPeriodFilterValues(search, defaultPeriod);
  return { dateFrom: toFilterText(search.dateFrom), dateTo: toFilterText(search.dateTo) };
}

export function toDonationApprovalFilterValues(
  search: DonationApprovalFilterSearch,
  defaultPeriod: DatePeriod | undefined,
): FilterValues {
  return {
    ...toPeriodValues(search, defaultPeriod),
    enteredBy: toFilterText(search.enteredBy),
    approveTypeNos: toNumberListFilterText(search.approveTypeNos),
    promoItemIds: toNumberListFilterText(search.promoItemIds),
    teacherMobile: toFilterText(search.teacherMobile),
    instituteEiin: toFilterText(search.instituteEiin),
    enteredMobile: toFilterText(search.enteredMobile),
  };
}

type DonationBatchFilterSearch = Pick<
  DonationBatchSearch,
  'dateFrom' | 'dateTo' | 'userId' | 'promoItemId'
>;

export function toDonationBatchFilterValues(search: DonationBatchFilterSearch): FilterValues {
  return {
    dateFrom: toFilterText(search.dateFrom),
    dateTo: toFilterText(search.dateTo),
    userId: toFilterText(search.userId),
    promoItemId: toFilterText(search.promoItemId),
  };
}

export function toDonationBatchFilterSearch(values: FilterValues): DonationBatchFilterSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
    promoItemId: parseNumberFilter(values.promoItemId),
  };
}

export type MpReportFilterSearch = Pick<
  MpReportSearch,
  'dateFrom' | 'dateTo' | 'userId' | 'divisionId' | 'districtId' | 'thanaId' | 'itemCode'
>;

type MpRegionFilterSearch = Pick<
  MpRegionSearch,
  'dateFrom' | 'dateTo' | 'rsmUserId' | 'divisionId' | 'districtId' | 'thanaId'
>;

export function toMpReportFilterValues(
  search: MpReportFilterSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    ...toPeriodFilterValues(search, defaultPeriod),
    userId: toFilterText(search.userId),
    ...toGeoFilterValues(search),
    itemCode: toFilterText(search.itemCode),
  };
}

export function toMpReportFilterSearch(values: FilterValues): MpReportFilterSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
    ...toGeoSearch(values),
    itemCode: parseTextFilter(values.itemCode),
  };
}

export function toMpRegionFilterValues(
  search: MpRegionFilterSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    ...toPeriodFilterValues(search, defaultPeriod),
    rsmUserId: toFilterText(search.rsmUserId),
    ...toGeoFilterValues(search),
  };
}

export function toMpRegionFilterSearch(values: FilterValues): MpRegionFilterSearch {
  return {
    ...toPeriodSearch(values),
    rsmUserId: parseNumberFilter(values.rsmUserId),
    ...toGeoSearch(values),
  };
}

type DonationRequisitionFilterSearch = Pick<
  DonationRequisitionSearch,
  'dateFrom' | 'dateTo' | 'instituteTypeNo'
>;

export function toDonationRequisitionFilterValues(
  search: DonationRequisitionFilterSearch,
): FilterValues {
  return {
    dateFrom: toFilterText(search.dateFrom),
    dateTo: toFilterText(search.dateTo),
    instituteTypeNo: toFilterText(search.instituteTypeNo),
  };
}

export function toDonationRequisitionFilterSearch(
  values: FilterValues,
): DonationRequisitionFilterSearch {
  return {
    ...toPeriodSearch(values),
    instituteTypeNo: parseNumberFilter(values.instituteTypeNo),
  };
}

type DonationBudgetFilterSearch = Pick<DonationBudgetSearch, 'userId' | 'promoItemId'>;

export function toDonationBudgetFilterValues(search: DonationBudgetFilterSearch): FilterValues {
  return {
    userId: toFilterText(search.userId),
    promoItemId: toFilterText(search.promoItemId),
  };
}

export function toDonationBudgetFilterSearch(values: FilterValues): DonationBudgetFilterSearch {
  return {
    userId: parseNumberFilter(values.userId),
    promoItemId: parseNumberFilter(values.promoItemId),
  };
}

export function toDonationApprovalFilterSearch(values: FilterValues): DonationApprovalFilterSearch {
  return {
    ...toPeriodSearch(values),
    enteredBy: parseNumberFilter(values.enteredBy),
    approveTypeNos: parseNumberListFilter(values.approveTypeNos),
    promoItemIds: parseNumberListFilter(values.promoItemIds),
    teacherMobile: parseTextFilter(values.teacherMobile),
    instituteEiin: parseTextFilter(values.instituteEiin),
    enteredMobile: parseTextFilter(values.enteredMobile),
  };
}
